import type { ConsentState } from "./consent";

export const STORAGE_KEY = "cmp_consent";
const CONSENT_ID_KEY = "cmp_user_key";
const POLICY_VERSION_KEY = "cmp_policy_version";
const BANNER_VERSION_KEY = "cmp_banner_version";

/** `localStorage.clear()` öncesi bu anahtarları yedekleyin; rıza ve visitor kimliği korunur. */
export const CMP_STORAGE_KEYS = {
  consent: STORAGE_KEY,
  userKey: CONSENT_ID_KEY,
  policyVersion: POLICY_VERSION_KEY,
  bannerVersion: BANNER_VERSION_KEY,
} as const;

export function snapshotCmpLocalStorage(): Record<string, string | null> {
  const out: Record<string, string | null> = {};
  for (const k of Object.values(CMP_STORAGE_KEYS)) {
    try {
      out[k] = localStorage.getItem(k);
    } catch {
      out[k] = null;
    }
  }
  return out;
}

export function restoreCmpLocalStorage(snapshot: Record<string, string | null>): void {
  for (const [k, v] of Object.entries(snapshot)) {
    if (v != null) {
      try {
        localStorage.setItem(k, v);
      } catch {
        /* ignore */
      }
    }
  }
}

export function loadConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return null;
    if ("categories" in (parsed as Record<string, unknown>)) {
      const payload = parsed as ConsentState;
      return {
        categories: payload.categories || {},
        cookies: payload.cookies || {},
      };
    }

    // Backward compatibility: old payload stored only categories.
    return {
      categories: parsed as Record<string, boolean>,
      cookies: {},
    };
  } catch {
    return null;
  }
}

export function saveConsent(consent: ConsentState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    // localStorage might be unavailable
    try {
      document.cookie = `${STORAGE_KEY}=${encodeURIComponent(JSON.stringify(consent))}; path=/; max-age=${365 * 24 * 60 * 60}; SameSite=Lax`;
    } catch {
      // Silently fail
    }
  }
}

export function clearConsent(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    document.cookie = `${STORAGE_KEY}=; path=/; max-age=0`;
  }
}

export function getUserKey(): string {
  let key = localStorage.getItem(CONSENT_ID_KEY);
  if (!key) {
    key = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(CONSENT_ID_KEY, key);
  }
  return key;
}

/** Rıza köprüsü: `cmp_user_key` yoksa üretir (getUserKey ile aynı). */
export function ensureCmpUserKey(): string {
  return getUserKey();
}

export function getStoredPolicyVersion(): number | null {
  try {
    const v = localStorage.getItem(POLICY_VERSION_KEY);
    return v ? parseInt(v, 10) : null;
  } catch {
    return null;
  }
}

export function savePolicyVersion(version: number): void {
  try {
    localStorage.setItem(POLICY_VERSION_KEY, String(version));
  } catch {}
}

export function getStoredBannerVersion(): number | null {
  try {
    const v = localStorage.getItem(BANNER_VERSION_KEY);
    return v ? parseInt(v, 10) : null;
  } catch {
    return null;
  }
}

export function saveBannerVersion(version: number): void {
  try {
    localStorage.setItem(BANNER_VERSION_KEY, String(version));
  } catch {}
}
